import React, { useState } from 'react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Search, Plus, Edit2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AddVendorToEventModal } from './AddVendorToEventModal';
import { EditEventVendorModal } from './EditEventVendorModal';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { useEventVendors, useDeleteEventVendor } from '@/hooks/useEventVendors';
import { formatCurrency } from '@/lib/utils/currency';
import { StatusBadge } from '@/components/status/StatusBadge';

interface EventVendorsTabProps {
  eventId: string;
}

export const EventVendorsTab: React.FC<EventVendorsTabProps> = ({ eventId }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [editingVendor, setEditingVendor] = useState<any>(null);
  const [vendorToDelete, setVendorToDelete] = useState<{id: string, name: string} | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const { toast } = useToast();
  const { data: eventVendors, isLoading: vendorsLoading } = useEventVendors(eventId);
  const deleteEventVendor = useDeleteEventVendor();

  const totalVendorCost = eventVendors?.reduce((sum, vendor) => sum + (vendor.cost || 0), 0) || 0;

  const filteredVendors = eventVendors?.filter(vendor =>
    vendor.vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.vendor.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.vendor.contact_person?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const handleDeleteVendor = async () => {
    if (!vendorToDelete || !eventId) return;
    try {
      await deleteEventVendor.mutateAsync({ id: vendorToDelete.id, eventId: eventId });
      setIsDeleteDialogOpen(false);
      setVendorToDelete(null);
    } catch (error) {
      console.error("Error deleting vendor:", error);
      toast({ title: "Error", description: "Failed to delete vendor", variant: "destructive" });
    }
  };

  return (
    <>
      <Card className="bg-white shadow-lg border-0">
        <CardHeader>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <CardTitle className="text-xl font-bold text-gray-900">Event Vendors</CardTitle>
              <p className="text-gray-600 mt-1">Manage vendors for this event</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-gray-600">Total Vendor Cost</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(totalVendorCost, "IN")}</p>
              </div>
              <AddVendorToEventModal eventId={eventId}>
                <Button className="bg-purple-600 hover:bg-purple-700 text-white">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Vendor
                </Button>
              </AddVendorToEventModal>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search vendors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Vendors ({filteredVendors.length})</h3>
            <p className="text-sm text-gray-600 mb-4">List of all vendors for this event</p>
          </div>

          {vendorsLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="medium" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vendor Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Contact Person</TableHead>
                  <TableHead>Contact Info</TableHead>
                  <TableHead>Event Cost</TableHead>
                  <TableHead>Payment Terms</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredVendors.map((eventVendor) => (
                  <TableRow key={eventVendor.id} className="hover:bg-gray-50">
                    <TableCell>
                      <div>
                        <div className="font-semibold text-gray-900">{eventVendor.vendor.name}</div>
                        <div className="text-sm text-gray-500">{eventVendor.vendor.description}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                        {eventVendor.vendor.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-900">{eventVendor.vendor.contact_person}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div className="text-gray-900">{eventVendor.vendor.phone}</div>
                        <div className="text-gray-500">{eventVendor.vendor.email}</div>
                      </div>
                    </TableCell>
                    <TableCell className="font-semibold text-green-600">
                      {formatCurrency(eventVendor.cost || 0, "IN")}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {eventVendor.payment_terms || "-"}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={eventVendor.status} className="text-[11px]" />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setEditingVendor(eventVendor)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-800"
                          onClick={() => {
                            setVendorToDelete({ id: eventVendor.id, name: eventVendor.vendor.name });
                            setIsDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {!vendorsLoading && filteredVendors.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">No vendors found for this event.</p>
            </div>
          )}
        </CardContent>
      </Card>

      <EditEventVendorModal
        isOpen={!!editingVendor}
        onClose={() => setEditingVendor(null)}
        eventVendor={editingVendor}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the vendor "{vendorToDelete?.name}" from this event.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setVendorToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteVendor} className="bg-red-600 hover:bg-red-700 text-white">
              Remove Vendor
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
